import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, CheckCircle2, Loader2, ShieldCheck } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { SectionHeader } from '@/components/ui/section-header';
import { SelectField } from '@/components/ui/select';
import { StatusPill } from '@/components/ui/status-pill';
import { form_error_message } from '@/lib/forms';
import { use_session_store } from '@/lib/session-store';
import { AdoptionPhotoEditor } from '@/components/adoption-photo-editor';
import {
  use_my_listing,
  use_listing_mutations,
  use_send_listing_otp,
  use_verify_listing_otp,
} from '@/features/adoption-listing/hooks';
import {
  SPECIES_OPTIONS,
  GENDER_OPTIONS,
  AGE_UNIT_OPTIONS,
  type AdoptionAgeUnit,
  type AdoptionGender,
  type AdoptionListingPayload,
  type AdoptionSpecies,
} from '@/features/adoption-listing/types';

type Form = {
  species: AdoptionSpecies | '';
  breed: string;
  age_value: string;
  age_unit: AdoptionAgeUnit;
  gender: AdoptionGender;
  vaccinated: boolean;
  sterilized: boolean;
  health_status: string;
  good_with: string;
  description: string;
  contact_name: string;
  contact_phone: string;
  address_line: string;
  city: string;
  state: string;
  pincode: string;
};

const EMPTY: Form = {
  species: '', breed: '', age_value: '', age_unit: 'MONTHS', gender: 'UNKNOWN',
  vaccinated: false, sterilized: false, health_status: '', good_with: '', description: '',
  contact_name: '', contact_phone: '', address_line: '', city: '', state: '', pincode: '',
};

const field =
  'rounded-input border-2 border-input bg-card px-4 py-3 text-base text-foreground shadow-[inset_0_2px_4px_rgba(40,23,26,0.04)] transition-all duration-fast focus-visible:border-primary focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-primary/15';

export function AdoptPostPage() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const phone = use_session_store((s) => s.phone) ?? undefined;
  const listing_q = use_my_listing(id);
  const { create, update } = use_listing_mutations();
  const send_otp = use_send_listing_otp(id ?? '');
  const verify_otp = use_verify_listing_otp(id ?? '');

  const [form, set_form] = useState<Form>(EMPTY);
  const [error, set_error] = useState<string | null>(null);
  const [otp_sent, set_otp_sent] = useState(false);
  const [otp_code, set_otp_code] = useState('');
  const [debug_code, set_debug_code] = useState<string | null>(null);

  // Prefill contact phone with the signed-in number for a new listing.
  useEffect(() => {
    if (!id && phone) set_form((f) => (f.contact_phone ? f : { ...f, contact_phone: phone }));
  }, [id, phone]);

  // Load an existing draft into the form.
  const listing = listing_q.data;
  useEffect(() => {
    if (!listing) return;
    set_form({
      species: listing.species,
      breed: listing.breed ?? '',
      age_value: listing.age_value != null ? String(listing.age_value) : '',
      age_unit: listing.age_unit ?? 'MONTHS',
      gender: listing.gender,
      vaccinated: listing.vaccinated ?? false,
      sterilized: listing.sterilized ?? false,
      health_status: listing.health_status ?? '',
      good_with: listing.good_with ?? '',
      description: listing.description ?? '',
      contact_name: listing.contact?.contact_name ?? '',
      contact_phone: listing.contact?.contact_phone ?? '',
      address_line: listing.contact?.address_line ?? '',
      city: listing.city,
      state: listing.state ?? '',
      pincode: listing.contact?.pincode ?? '',
    });
  }, [listing]);

  const published = listing?.status === 'PUBLISHED';

  function payload(): AdoptionListingPayload | null {
    if (!form.species) { set_error('Please choose a species.'); return null; }
    if (!form.contact_name.trim()) { set_error('Please add a contact name.'); return null; }
    if (!form.contact_phone.trim()) { set_error('Please add a contact phone.'); return null; }
    if (!form.city.trim()) { set_error('Please add a city.'); return null; }
    return {
      species: form.species,
      contact_name: form.contact_name.trim(),
      contact_phone: form.contact_phone.trim(),
      city: form.city.trim(),
      breed: form.breed.trim() || undefined,
      age_value: form.age_value.trim() ? Number(form.age_value) : undefined,
      age_unit: form.age_value.trim() ? form.age_unit : undefined,
      gender: form.gender,
      vaccinated: form.vaccinated,
      sterilized: form.sterilized,
      health_status: form.health_status.trim() || undefined,
      good_with: form.good_with.trim() || undefined,
      description: form.description.trim() || undefined,
      address_line: form.address_line.trim() || undefined,
      state: form.state.trim() || undefined,
      pincode: form.pincode.trim() || undefined,
    };
  }

  async function save() {
    set_error(null);
    const body = payload();
    if (!body) return;
    try {
      if (id) {
        await update.mutateAsync({ id, input: body });
      } else {
        const created = await create.mutateAsync(body);
        navigate(`/app/adopt/edit/${created.id}`, { replace: true });
      }
    } catch (err) {
      set_error(form_error_message(err, 'Could not save the listing.'));
    }
  }

  async function do_send_otp() {
    set_error(null);
    try {
      const res = await send_otp.mutateAsync();
      set_otp_sent(true);
      set_debug_code(res.debug_otp_code ?? null);
    } catch (err) {
      set_error(form_error_message(err, 'Could not send the code.'));
    }
  }

  async function do_verify() {
    set_error(null);
    try {
      await verify_otp.mutateAsync(otp_code.trim());
    } catch (err) {
      set_error(form_error_message(err, 'That code did not work. Please try again.'));
    }
  }

  const saving = create.isPending || update.isPending;

  return (
    <div className="mx-auto flex w-full max-w-2xl flex-col gap-5">
      <Button variant="ghost" size="sm" className="self-start" onClick={() => navigate('/app/adopt/mine')}>
        <ArrowLeft className="h-4 w-4" /> My listings
      </Button>

      <SectionHeader
        title={id ? 'Edit your listing' : 'Post a pet for adoption'}
        description="Share a few details so this pet can find a loving home. You'll verify your number before it goes live."
      />

      {published && (
        <div className="flex items-center gap-2 rounded-card border-2 border-success/30 bg-success/5 p-3 text-sm text-success" data-testid="listing-published-banner">
          <CheckCircle2 className="h-4 w-4" /> This listing is live in the Adopt feed.
        </div>
      )}

      {/* Pet details */}
      <Card>
        <CardContent className="flex flex-col gap-4 p-5">
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="species">Species</Label>
              <SelectField id="species" data-testid="adopt-species" value={form.species} placeholder="Choose"
                onValueChange={(v) => set_form((f) => ({ ...f, species: v as AdoptionSpecies }))} options={SPECIES_OPTIONS} />
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="breed">Breed <span className="text-xs font-normal text-muted-foreground">(optional)</span></Label>
              <Input id="breed" data-testid="adopt-breed" maxLength={120} value={form.breed} onChange={(e) => set_form((f) => ({ ...f, breed: e.target.value }))} placeholder="Indie / Labrador…" />
            </div>
            <div className="flex flex-col gap-1.5">
              <Label>Approx. age <span className="text-xs font-normal text-muted-foreground">(optional)</span></Label>
              <div className="flex gap-2">
                <Input type="number" min={0} className="w-24" data-testid="adopt-age-value" value={form.age_value} onChange={(e) => set_form((f) => ({ ...f, age_value: e.target.value }))} placeholder="3" />
                <SelectField data-testid="adopt-age-unit" value={form.age_unit} onValueChange={(v) => set_form((f) => ({ ...f, age_unit: v as AdoptionAgeUnit }))} options={AGE_UNIT_OPTIONS} className="flex-1" />
              </div>
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="gender">Gender</Label>
              <SelectField id="gender" data-testid="adopt-gender" value={form.gender} onValueChange={(v) => set_form((f) => ({ ...f, gender: v as AdoptionGender }))} options={GENDER_OPTIONS} />
            </div>
          </div>

          <div className="flex flex-wrap gap-3">
            <label className="flex cursor-pointer items-center gap-2 rounded-input border-2 border-input bg-secondary/40 px-3 py-2 text-sm font-medium">
              <input type="checkbox" data-testid="adopt-vaccinated" checked={form.vaccinated} onChange={(e) => set_form((f) => ({ ...f, vaccinated: e.target.checked }))} className="h-4 w-4 accent-primary" /> Vaccinated
            </label>
            <label className="flex cursor-pointer items-center gap-2 rounded-input border-2 border-input bg-secondary/40 px-3 py-2 text-sm font-medium">
              <input type="checkbox" data-testid="adopt-sterilized" checked={form.sterilized} onChange={(e) => set_form((f) => ({ ...f, sterilized: e.target.checked }))} className="h-4 w-4 accent-primary" /> Sterilised
            </label>
          </div>

          <div className="flex flex-col gap-1.5">
            <Label htmlFor="health">Health status <span className="text-xs font-normal text-muted-foreground">(optional)</span></Label>
            <Input id="health" data-testid="adopt-health" value={form.health_status} onChange={(e) => set_form((f) => ({ ...f, health_status: e.target.value }))} placeholder="Healthy, dewormed…" />
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="desc">About this pet <span className="text-xs font-normal text-muted-foreground">(optional)</span></Label>
            <textarea id="desc" data-testid="adopt-description" rows={3} className={field} value={form.description} onChange={(e) => set_form((f) => ({ ...f, description: e.target.value }))} placeholder="Temperament, story, why they need a home…" />
          </div>
        </CardContent>
      </Card>

      {/* Contact + location */}
      <Card>
        <CardContent className="flex flex-col gap-4 p-5">
          <p className="text-sm font-semibold">Contact &amp; location</p>
          <p className="-mt-2 text-xs text-muted-foreground">Your phone &amp; address stay hidden until an adopter pays the platform fee to reach you.</p>
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="cname">Contact name</Label>
              <Input id="cname" data-testid="adopt-contact-name" value={form.contact_name} onChange={(e) => set_form((f) => ({ ...f, contact_name: e.target.value }))} />
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="cphone">Contact phone</Label>
              <Input id="cphone" data-testid="adopt-contact-phone" value={form.contact_phone} onChange={(e) => set_form((f) => ({ ...f, contact_phone: e.target.value }))} placeholder="+91…" />
            </div>
            <div className="flex flex-col gap-1.5 sm:col-span-2">
              <Label htmlFor="addr">Address <span className="text-xs font-normal text-muted-foreground">(optional)</span></Label>
              <Input id="addr" data-testid="adopt-address" value={form.address_line} onChange={(e) => set_form((f) => ({ ...f, address_line: e.target.value }))} />
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="city">City</Label>
              <Input id="city" data-testid="adopt-city" value={form.city} onChange={(e) => set_form((f) => ({ ...f, city: e.target.value }))} placeholder="Bengaluru" />
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="pincode">Pincode <span className="text-xs font-normal text-muted-foreground">(optional)</span></Label>
              <Input id="pincode" data-testid="adopt-pincode" value={form.pincode} onChange={(e) => set_form((f) => ({ ...f, pincode: e.target.value }))} />
            </div>
          </div>

          {error && <p className="text-sm text-destructive" data-testid="adopt-form-error">{error}</p>}

          <div>
            <Button onClick={save} variant="gradient" disabled={saving} data-testid="adopt-save">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
              {id ? 'Save changes' : 'Save & add photos'}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Photos + publish (only once the draft exists) */}
      {id && (
        <Card>
          <CardContent className="flex flex-col gap-4 p-5">
            <AdoptionPhotoEditor listing_id={id} />
          </CardContent>
        </Card>
      )}

      {id && !published && (
        <Card>
          <CardContent className="flex flex-col gap-3 p-5" data-testid="adopt-publish-card">
            <div className="flex items-start gap-2">
              <ShieldCheck className="mt-0.5 h-5 w-5 shrink-0 text-primary" />
              <div>
                <p className="text-sm font-semibold">Verify your number to publish</p>
                <p className="text-xs text-muted-foreground">We'll text a code to {form.contact_phone || 'your contact number'} to confirm it's really you.</p>
              </div>
            </div>

            {!otp_sent ? (
              <div>
                <Button variant="outline" size="sm" onClick={do_send_otp} disabled={send_otp.isPending} data-testid="adopt-send-otp">
                  {send_otp.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : null} Send code
                </Button>
              </div>
            ) : (
              <div className="flex flex-col gap-2">
                {debug_code && (
                  <p className="rounded-input border-2 border-dashed border-primary/30 bg-primary/5 px-3 py-2 text-sm" data-testid="adopt-debug-otp">
                    Dev code: <span className="font-mono font-bold tracking-widest">{debug_code}</span>
                  </p>
                )}
                <div className="flex gap-2">
                  <Input className="w-40 tracking-[0.3em]" placeholder="000000" value={otp_code} onChange={(e) => set_otp_code(e.target.value)} data-testid="adopt-otp-input" />
                  <Button variant="gradient" onClick={do_verify} disabled={verify_otp.isPending || otp_code.trim().length < 4} data-testid="adopt-verify-otp">
                    {verify_otp.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : null} Verify &amp; publish
                  </Button>
                </div>
              </div>
            )}
            {error && <p className="text-sm text-destructive" data-testid="adopt-publish-error">{error}</p>}
            <p className="text-xs text-muted-foreground">Status: <StatusPill tone="neutral">{listing?.status.toLowerCase()}</StatusPill></p>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
